import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../contexts/ThemeContext';
import { authFetch } from '../../services/authFetch';



const PSMForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null, isEdit = false, onSubmit = null }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    psm_date: '',
    start_time: '',
    finish_time: '',
    apresiasis_id: '',
    description: '',
    reflection: '',
    pic: '',
    pic_contact_number: '',
    images: [],
    location: '',
    admin_note: ''
  });

  const [apresiasiOptions, setApresiasiOptions] = useState([]);
  const [reflectionCharCount, setReflectionCharCount] = useState(0);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [loading, setLoading] = useState(false);
  const [imageValidation, setImageValidation] = useState({ 
    hasValidImages: false, 
    errorMessage: '', 
    isPristine: true 
  });

  useEffect(() => {
    const fetchApresiasi = async () => {
      try {
        const response = await authFetch('/api/apresiasis-view');
        let data = [];
        try {
          data = await response.json();
        } catch (err) {
          data = [];
        }
        setApresiasiOptions(data);
      } catch (err) {
        setApresiasiOptions([]);
      }
    };
    fetchApresiasi();
  }, []);

  useEffect(() => {
    setReflectionCharCount(formData.reflection?.length || 0);
  }, [formData.reflection]);

  useEffect(() => {
    if (initialData) {
      // Make sure images is properly initialized from API response
      const images = initialData.images || [];
      setFormData({
        ...initialData,
        images
      });
    }
  }, [initialData]);

  // Validate images whenever they change
  useEffect(() => {
    const hasImages = formData.images && formData.images.length > 0;
    setImageValidation({
      hasValidImages: hasImages,
      errorMessage: hasImages ? '' : 'Foto bukti kegiatan wajib diunggah (minimal 1 foto).',
      isPristine: false
    });
  }, [formData.images]);

  const handleFileChange = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setNotification({ show: true, message: 'Hanya file gambar yang diperbolehkan.', type: 'error' });
      return;
    }

    // No total file size limit - individual files will be compressed as needed

    const compressedFiles = await Promise.all(
      imageFiles.map(async (file) => {
        // Always compress files larger than 500KB for mobile optimization
        if (file.size > 512 * 1024) {
          try {
            // Mobile-optimized compression settings
            const options = {
              maxSizeMB: 0.3, // Smaller size for mobile
              maxWidthOrHeight: 800, // Smaller dimensions for mobile
              useWebWorker: false, // Disable web worker for mobile compatibility
              initialQuality: 0.8,
            };
            const compressedFile = await imageCompression(file, options);
             
             // Ensure compressed file is a proper File object, not a Blob
             let finalFile;
             if (compressedFile instanceof File) {
               finalFile = compressedFile;
             } else {
               // Convert Blob to File with proper metadata
               finalFile = new File([compressedFile], file.name, {
                 type: compressedFile.type || file.type || 'image/jpeg',
                 lastModified: Date.now()
               });
             }
             
             // Validate that we have a proper File object
             if (!(finalFile instanceof File)) {
               console.error('Failed to create proper File object from compressed image');
               throw new Error('Invalid file type after compression');
             }
             
             return finalFile;
          } catch (error) {
            console.error('Error during image compression:', error);
            // Fallback: try basic compression without web worker
            try {
              const fallbackOptions = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 1024,
                useWebWorker: false,
                initialQuality: 0.6
              };
              const fallbackFile = await imageCompression(file, fallbackOptions);
               
               // Ensure fallback file is also a proper File object
               let finalFallbackFile;
               if (fallbackFile instanceof File) {
                 finalFallbackFile = fallbackFile;
               } else {
                 // Convert Blob to File with proper metadata
                 finalFallbackFile = new File([fallbackFile], file.name, {
                   type: fallbackFile.type || file.type || 'image/jpeg',
                   lastModified: Date.now()
                 });
               }
               
               // Validate fallback file
               if (!(finalFallbackFile instanceof File)) {
                 console.error('Failed to create proper File object from fallback compression');
                 throw new Error('Invalid fallback file type');
               }
               
               return finalFallbackFile;
            } catch (fallbackError) {
              console.error('Fallback compression failed:', fallbackError);
              setNotification({ show: true, message: `Gagal mengompres ${file.name}. File akan dilewati.`, type: 'error' });
              return null;
            }
          }
        }
        return file;
      })
    );

    const validFiles = compressedFiles.filter(file => file !== null && file instanceof File);

    if (validFiles.length === 0) {
      setNotification({ show: true, message: 'Tidak ada file yang valid untuk diupload.', type: 'error' });
      return;
    }

    setFormData(prev => ({
      ...prev,
      images: [...prev.images.filter(img => img instanceof File), ...validFiles].slice(0, 5) // Only keep File objects
    }));
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    
    if (name === 'reflection') {
      setFormData({ ...formData, reflection: value });
      setReflectionCharCount(value.length);
    } else if (name === 'pic_contact_number') {
      if (/^\d*$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification({ show: false, message: '', type: 'success' });
    if (readOnly) {
      onClose();
      return;
    }
    
    // If this is edit mode and onSubmit callback is provided, use it instead of internal logic
    if (isEdit && onSubmit) {
      // Validate images for edit mode
      if (!imageValidation.hasValidImages) {
        setNotification({ show: true, message: imageValidation.errorMessage, type: 'error' });
        return;
      }
      
      setLoading(true);
      try {
        await onSubmit(formData);
        // Don't close the modal here - let the parent handle it after successful update
      } catch (error) {
        setNotification({ show: true, message: error.message || 'Gagal memperbarui laporan.', type: 'error' });
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Original create logic
    if (reflectionCharCount < 200) {
      setNotification({ show: true, message: 'Refleksi harus minimal 200 karakter.', type: 'error' });
      return;
    }

    // Check for cross-midnight scenarios and warn user
    if (formData.start_time && formData.finish_time) {
      const [startHour, startMin] = formData.start_time.split(':').map(Number);
      const [finishHour, finishMin] = formData.finish_time.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const finishMinutes = finishHour * 60 + finishMin;
      
      // If finish time is earlier than start time, it's a cross-midnight scenario
      if (finishMinutes <= startMinutes) {
        setNotification({ 
          show: true, 
          message: 'Waktu selesai tidak boleh lebih awal dari waktu mulai. Untuk kegiatan lintas tengah malam, silakan buat laporan terpisah.', 
          type: 'error' 
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Validate and convert images before sending
       const validImages = [];
       for (const img of formData.images) {
         if (img instanceof File) {
           validImages.push(img);
         } else if (img instanceof Blob) {
           // Convert Blob to File if somehow a Blob made it through
           console.warn('Converting Blob to File object:', img);
           const fileName = `image_${Date.now()}.jpg`;
           const fileFromBlob = new File([img], fileName, {
             type: img.type || 'image/jpeg',
             lastModified: Date.now()
           });
           validImages.push(fileFromBlob);
         } else if (typeof img === 'string' || (img && img.image_url)) {
           // Skip existing images from server (these are for display only)
           console.log('Skipping existing server image:', img);
         } else {
           console.warn('Invalid image type detected and filtered out:', img);
         }
       }
       
       if (validImages.length === 0) {
         const message = formData.images.length > 0
           ? 'Tidak ada gambar valid untuk diupload.'
           : 'Foto bukti kegiatan wajib diunggah (minimal 1 foto).';
         setNotification({ show: true, message, type: 'error' });
         setLoading(false);
         return;
       }

      // Recreate FormData with validated images
      const validatedData = new FormData();
      validatedData.append('psm_date', formData.psm_date);
      validatedData.append('start_time', formData.start_time);
      validatedData.append('finish_time', formData.finish_time);
      validatedData.append('apresiasis_id', formData.apresiasis_id);
      validatedData.append('location', formData.location);
      validatedData.append('description', formData.description);
      validatedData.append('reflection', formData.reflection);
      validatedData.append('pic', formData.pic);
      if (formData.pic_contact_number) validatedData.append('pic_contact_number', formData.pic_contact_number);
      
      // Only append valid File objects
      validImages.forEach((file) => {
        validatedData.append('images[]', file, file.name);
      });

      // Debug: Log the exact data being sent to API
      console.log('FormData being sent to API:', {
        psm_date: validatedData.get('psm_date'),
        start_time: validatedData.get('start_time'),
        finish_time: validatedData.get('finish_time'),
        apresiasis_id: validatedData.get('apresiasis_id'),
        location: validatedData.get('location'),
        description: validatedData.get('description'),
        reflection: validatedData.get('reflection'),
        pic: validatedData.get('pic'),
        pic_contact_number: validatedData.get('pic_contact_number')
      });

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000); // 60 second timeout for mobile

      const response = await authFetch('/api/psm_reports', {
        method: 'POST',
        body: validatedData,
        signal: controller.signal,
        // Remove Content-Type header to let browser set it automatically for FormData
        headers: {}
      });
      
      clearTimeout(timeoutId);
      
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('JSON parse error:', parseErr, 'Response:', responseText);
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setNotification({ show: true, message: data.message || 'Gagal mengirim laporan PSM.', type: 'error' });
      } else {
        setNotification({ show: true, message: data.message || 'Laporan PSM berhasil dikirim!', type: 'success' });
        
        // Clean up blob URLs to prevent memory leaks
        formData.images.forEach(img => {
          if (img instanceof File && img.preview) {
            URL.revokeObjectURL(img.preview);
          }
        });
        
        setFormData({
          psm_date: '',
          start_time: '',
          finish_time: '',
          apresiasis_id: '',
          description: '',
          reflection: '',
          pic: '',
          pic_contact_number: '',
          images: [],
          location: '',
          admin_note: ''
        });
        setReflectionCharCount(0);
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      if (err.name === 'AbortError') {
        setNotification({ show: true, message: 'Upload timeout. Silakan coba lagi dengan gambar yang lebih kecil.', type: 'error' });
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setNotification({ show: true, message: 'Koneksi terputus. Periksa jaringan internet Anda.', type: 'error' });
      } else {
        setNotification({ show: true, message: 'Terjadi kesalahan jaringan. Silakan coba lagi.', type: 'error' });
      }
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>{isEdit ? 'Edit Laporan PSM' : 'Formulir Laporan PSM'}</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>
      
      <FormContent>
      {notification.show && (
          <NotificationToast $type={notification.type}>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
              ×
            </CloseNotificationButton>
          </NotificationToast>
        )}
        <form onSubmit={handleSubmit}>
          {/* Tanggal PSM */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="psm_date">Tanggal PSM</label>
            <input
              type="date"
              id="psm_date"
              name="psm_date"
              value={formData.psm_date || ''}
              onChange={handleChange}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          {/* Waktu mulai & selesai */}
          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="start_time">Jam Mulai</label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time || ''}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="finish_time">Jam Selesai</label>
              <input
                type="time"
                id="finish_time"
                name="finish_time"
                value={formData.finish_time || ''}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>
          
          {/* Jenis Apresiasi */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="apresiasis_id">Jenis Apresiasi</label>
            <select
              id="apresiasis_id"
              name="apresiasis_id"
              value={formData.apresiasis_id || ''}
              onChange={handleChange}
              required
              disabled={readOnly}
              readOnly={readOnly}
            >
              <option value="">Pilih jenis apresiasi</option>
              {apresiasiOptions && Array.isArray(apresiasiOptions) && apresiasiOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.value}</option>
              ))}
            </select>
          </FormGroup>
          
          {/* Lokasi Kegiatan */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="location">Lokasi Kegiatan</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location || ''}
              onChange={handleChange}
              placeholder="Masukkan tempat/lokasi kegiatan"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          {/* Rincian / Deskripsi */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="description">Rincian Kegiatan</label>
            <textarea
              id="description"
              name="description"
              rows="3"
              value={formData.description || ''}
              onChange={handleChange}
              placeholder="Masukkan rincian kegiatan atau apresiasi"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          {/* Refleksi */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="reflection">Refleksi</label>
            <textarea
              id="reflection"
              name="reflection"
              rows="4"
              value={formData.reflection || ''}
              onChange={handleChange}
              placeholder="Tuliskan refleksi diri tentang kegiatan PSM yang telah dilakukan. Min 200 karakter."
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
            <div style={{ fontSize: '0.85rem', color: reflectionCharCount < 500 ? '#dc3545' : '#4caf50', marginTop: 4 }}>
              {reflectionCharCount} / 200 karakter
            </div>
          </FormGroup>
          
          {/* Penanggung Jawab */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="pic">Penanggung Jawab</label>
            <input
              type="text"
              id="pic"
              name="pic"
              value={formData.pic || ''}
              onChange={handleChange}
              placeholder="Masukkan nama penanggung jawab"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          {/* Nomor Telepon Penanggung Jawab */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="pic_contact_number">Nomor Telepon Penanggung Jawab</label>
            <input
              type="text"
              id="pic_contact_number"
              name="pic_contact_number"
              value={formData.pic_contact_number || ''}
              onChange={handleChange}
              placeholder="Masukkan nomor telepon"
              required
              readOnly={readOnly}
              disabled={readOnly}
              inputMode="numeric"
              maxLength={15}
            />
          </FormGroup>
          
          {/* Upload Bukti (Multiple Images) */}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="images">Foto bukti kegiatan, wajib dengan pendamping (bisa lebih dari 1 foto)</label>
            <input
              type="file"
              id="images"
              name="images"
              multiple
              onChange={handleFileChange}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              disabled={readOnly}
              required={!isEdit || formData.images.length === 0}
            />
            <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
              Minimal 1 foto, maksimal 5 foto. Format: JPG, PNG, WebP
            </div>
            {!imageValidation.hasValidImages && !imageValidation.isPristine && (
              <ImageValidationError>{imageValidation.errorMessage}</ImageValidationError>
            )}
            {imageValidation.hasValidImages && (
              <ImageValidationSuccess>✓ {formData.images.length} foto terunggah</ImageValidationSuccess>
            )}
            <ImagePreviewContainer>
              {formData.images && formData.images.map((image, index) => {
                // Handle different image formats
                let imgSrc = '';
                let shouldCleanup = false;

                const withCacheBust = (raw) => {
                  const cacheBust = `t=${Date.now()}`;
                  return raw.includes('?') ? `${raw}&${cacheBust}` : `${raw}?${cacheBust}`;
                };

                const resolveUrl = (urlLike) => {
                  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                  if (!urlLike) return '';
                  const rawUrl = urlLike.startsWith('http')
                    ? urlLike
                    : `${baseUrl}${urlLike.startsWith('/') ? '' : '/'}${urlLike}`;
                  return withCacheBust(rawUrl);
                };

                if (image instanceof File) {
                  imgSrc = URL.createObjectURL(image);
                  shouldCleanup = true;
                } else if (typeof image === 'string') {
                  // Support relative and absolute string URLs with cache busting
                  imgSrc = resolveUrl(image);
                } else if (image && (image.image_url || image.url || image.path)) {
                  // Support various server image object shapes: image_url, url, path
                  const candidate = image.image_url || image.url || image.path;
                  imgSrc = resolveUrl(candidate);
                }
                
                return (
                  <ImagePreview key={index}>
                    <img 
                      src={imgSrc} 
                      alt={`preview ${index}`}
                      onError={(e) => {
                        console.error('Image failed to load:', imgSrc);
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        // Clean up blob URL after image loads to prevent memory leaks
                        if (shouldCleanup && imgSrc.startsWith('blob:')) {
                          setTimeout(() => URL.revokeObjectURL(imgSrc), 1000);
                        }
                      }}
                    />
                    {!readOnly && (
                      <RemoveImageButton 
                        onClick={() => {
                          // Allow removal even if this is the last image.
                          // File input will be required when images become empty.
                          if (image instanceof File && imgSrc.startsWith('blob:')) {
                            URL.revokeObjectURL(imgSrc);
                          }
                          setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
                        }}
                        title={'Hapus foto'}
                      >
                        ×
                      </RemoveImageButton>
                    )}
                  </ImagePreview>
                );
              })}
            </ImagePreviewContainer>
          </FormGroup>
          
          {/* Admin Note - Read Only */}
          {formData.admin_note && (
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="admin_note">Catatan Admin</label>
              <textarea
                id="admin_note"
                name="admin_note"
                rows="3"
                value={formData.admin_note || ''}
                readOnly
                disabled
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed'
                }}
              />
            </FormGroup>
          )}
          
          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading || readOnly || !imageValidation.hasValidImages}>
                {loading ? (isEdit ? 'Menyimpan...' : 'Mengirim...') : (readOnly ? 'Tutup' : (isEdit ? 'Simpan Perubahan' : 'Kirim Laporan'))}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const ErrorMessage = styled.div`
  background-color: #feeeee;
  color: #dc3545;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid #dc3545;
  display: flex;
  align-items: center;
  &::before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
  
  // Custom scrollbar for webkit browsers
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => (theme === 'dark' ? '#2c3e50' : '#ecf0f1')};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => (theme === 'dark' ? '#4a627a' : '#bdc3c7')};
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => (theme === 'dark' ? '#5a7896' : '#a5aab0')};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${ImagePreview}:hover & {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const ImageValidationError = styled.div`
  background-color: #feeeee;
  color: #dc3545;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #dc3545;
  display: flex;
  align-items: center;
  &::before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const ImageValidationSuccess = styled.div`
  background-color: #e6f8f5;
  color: #00a389;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #00a389;
  display: flex;
  align-items: center;
  &::before {
    content: "✓";
    margin-right: 0.5rem;
    font-weight: bold;
  }
`;

export default PSMForm;